/* eslint-disable max-lines-per-function, complexity, no-restricted-syntax, react-hooks/exhaustive-deps, react/jsx-no-leaked-render -- TODO(karl): [NX-7080] legacy subscribe page predates current lint config (useState naming, arrow function size, leaked render guards). Disabled file-wide pending refactor under NX-7080; do not propagate this pattern to new files. */
import { useTranslation, useUserInfo } from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { SubscriptionPlatform } from "@next-platform/packages-ts-types";
import { Chip, Spacer, Spinner, Tab, Tabs } from "@nextui-org/react";
import { Key, useCallback, useState } from "react";

import { LocalizedLink } from "@/components";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import TextStyles from "@/components/common/TextStyles";
import { Col, Container, Row } from "@/components/customComponents/index";
import Trans from "@/components/Trans/Trans";
import {
  COUNTRY_SPECFIC_SUBSCRIPTION_MESSAGES,
  SUBSCRIBE_USING_CRYPTO_PAGE_ROUTE,
  SUBSCRIPTION_BILLING_PERIOD_TYPE,
  USER_ROLES,
  USER_SUBSCRIPTION_STATUS,
} from "@/constants";
import { usePaywallViewedTracking } from "@/features/tracking/usePaywallViewedTracking";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import useManageSubscription from "@/hooks/useManageSubscription";
import usePurchaseOptions from "@/hooks/usePurchaseOptions";
import { cn, getCookie, getPurchaseData } from "@/utils";

import PaymentMethodsDisplay from "./components/PaymentMethodsDisplay";
import PlanComparisonTable from "./components/PlanComparisonTable";
import SubscriptionCard from "./components/SubscriptionCard";
import SubscriptionFAQs from "./components/SubscriptionFAQs";

const COUNTRY = getCookie("country") as keyof typeof COUNTRY_SPECFIC_SUBSCRIPTION_MESSAGES;

const tabClassNames = {
  tabList: `bg-gray-50 p-1 mob:p-1.5 max-mob:!h-auto`,
  tab: "max-mob:!h-auto",
  tabContent: "group-data-[selected=true]:font-medium text-sm mob:text-2xl max-mob:!whitespace-normal max-mob:text-center",
};

const Subscribe = () => {
  // Fire Meta `ViewContent` once per paywall mount so the mid-funnel
  // signal flows into Ads optimization, retargeting audiences, and
  // lookalike seeds. Tagged via `content_name` / `content_category`
  // to keep the audience pool isolated from any future unrelated
  // `ViewContent` fires elsewhere in the app.
  usePaywallViewedTracking();
  const { purchaseOptions } = usePurchaseOptions();
  const { pagePath } = useLocalizedLocation();
  const { t } = useTranslation();
  const isSubscribeUsingCryptoPage = pagePath === SUBSCRIBE_USING_CRYPTO_PAGE_ROUTE;

  type BillingPeriod = (typeof SUBSCRIPTION_BILLING_PERIOD_TYPE)[keyof typeof SUBSCRIPTION_BILLING_PERIOD_TYPE];

  const [selectedFrequency, setSelectedFrequency] = useState<BillingPeriod>(SUBSCRIPTION_BILLING_PERIOD_TYPE.MONTHLY);

  const { manageLinkHref, isExternalManageLink, shouldShowManage } = useManageSubscription();

  const { status: subscriptionStatus, platform: subscriptionPlatform } = useUserInfo();

  const isUserPlanActive = subscriptionStatus === USER_SUBSCRIPTION_STATUS.ACTIVE && subscriptionPlatform !== SubscriptionPlatform.REVENUE_CAT;
  const annualDiscount = purchaseOptions?.annual_discount;
  const quarterlyDiscount = purchaseOptions?.quarterly_discount;
  const isQuarterlyPlanAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.BillingQuarterlyPlan);
  const hasQuarterlyPlans = Boolean(purchaseOptions?.quarterly_plans?.length) && isQuarterlyPlanAllowed;
  const effectiveFrequency =
    !hasQuarterlyPlans && selectedFrequency === SUBSCRIPTION_BILLING_PERIOD_TYPE.QUARTERLY
      ? SUBSCRIPTION_BILLING_PERIOD_TYPE.MONTHLY
      : selectedFrequency;
  const isBillingPeriodAnnual = effectiveFrequency === SUBSCRIPTION_BILLING_PERIOD_TYPE.ANNUAL;
  const isBillingPeriodQuarterly = effectiveFrequency === SUBSCRIPTION_BILLING_PERIOD_TYPE.QUARTERLY;
  const selectedPurchaseOptions = purchaseOptions?.[effectiveFrequency];
  const memoizedSetSelectedFrequency = useCallback(
    () => (key: Key) => {
      setSelectedFrequency(key as BillingPeriod);
    },
    [selectedFrequency],
  );
  if (!purchaseOptions) {
    return <Spinner className={TextStyles.spinner} />;
  }

  return (
    <>
      <PageStaticMetaTags pageId="subscribePage" />
      {isUserPlanActive ? (
        <Row className="w-full flex-col mob:flex-row items-center mob:p-4 justify-between bg-[linear-gradient(270deg,#00438F80_0%,#0072F590_100%)] rounded-[10px] p-2 px-6">
          <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className="mob:text-lg text-white">
            <Trans i18nKey="subscription:page.activeSubscriptionMessage" />
          </Text>
          {shouldShowManage && manageLinkHref ? (
            <LocalizedLink
              to={manageLinkHref}
              target={isExternalManageLink ? "_blank" : undefined}
              rel={isExternalManageLink ? "noopener noreferrer" : undefined}
              data-testid="Subscribe-ManageSubscriptionLink"
            >
              <Text variant={TextVariants.HEADING_6_BOLD} className="text-sm mob:text-lg underline text-white">
                <Trans i18nKey="subscription:page.manageSubscription" />
              </Text>
            </LocalizedLink>
          ) : null}
        </Row>
      ) : null}
      <Container className="items-center p-5">
        {!isUserPlanActive ? <Spacer y={4} /> : null}
        <Text variant={TextVariants.MOBILE_HEADING_4_BOLD} className="text-white mob:text-[38px]">
          <Trans i18nKey="subscription:web.page.title" />
        </Text>
        <Spacer y={4} />
        <Text variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR} className="text-center mob:text-2xl">
          <Trans i18nKey="subscription:web.page.description" />
        </Text>
        <Spacer y={2} />
        {COUNTRY_SPECFIC_SUBSCRIPTION_MESSAGES[COUNTRY]?.LABEL && (
          <>
            <Spacer y={6} />
            <Col align="center" className="gap-6">
              <Text variant={TextVariants.PARAGRAPH_MEDIUM_BOLD} className="mob:text-xl">
                {COUNTRY_SPECFIC_SUBSCRIPTION_MESSAGES[COUNTRY]?.LABEL}{" "}
                <Text as="span" variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR} className="mob:text-xl">
                  {COUNTRY_SPECFIC_SUBSCRIPTION_MESSAGES[COUNTRY]?.SUB_LABEL}
                </Text>
              </Text>
            </Col>
            <Spacer y={1} />
          </>
        )}
        <Spacer className="!mt-6 mob:!mt-12" />
        <Tabs radius="full" selectedKey={effectiveFrequency} onSelectionChange={memoizedSetSelectedFrequency()} classNames={tabClassNames} size="sm">
          <Tab
            key={SUBSCRIPTION_BILLING_PERIOD_TYPE.ANNUAL}
            title={
              <>
                <Trans i18nKey="subscription:page.payAnnually" />{" "}
                <Chip className={`bg-blue-400 mob:py-lg ${!isBillingPeriodAnnual && "bg-opacity-disabled"}`} size="sm">
                  <Text
                    variant={TextVariants.PARAGRAPH_SMALL_REGULAR}
                    className={cn("text-xs mob:text-xl", !isBillingPeriodAnnual ? "opacity-50" : "opacity-100")}
                  >
                    <Trans i18nKey="subscription:page.saveDiscount" values={{ discount: annualDiscount }} />
                  </Text>
                </Chip>
              </>
            }
            className="bg-transparent mob:py-[22px]"
          />
          {hasQuarterlyPlans ? (
            <Tab
              key={SUBSCRIPTION_BILLING_PERIOD_TYPE.QUARTERLY}
              data-testid="SubscriptionQuarterlyTab"
              title={
                <>
                  <Trans i18nKey="subscription:page.payQuarterly" />{" "}
                  {quarterlyDiscount ? (
                    <Chip className={`bg-blue-400 mob:py-lg ${!isBillingPeriodQuarterly && "bg-opacity-disabled"}`} size="sm">
                      <Text
                        variant={TextVariants.PARAGRAPH_SMALL_REGULAR}
                        className={cn("text-xs mob:text-xl", !isBillingPeriodQuarterly ? "opacity-50" : "opacity-100")}
                      >
                        <Trans i18nKey="subscription:page.saveDiscount" values={{ discount: quarterlyDiscount }} />
                      </Text>
                    </Chip>
                  ) : null}
                </>
              }
              className="bg-transparent py-2 mob:py-5 mob:pb-6"
            />
          ) : null}
          <Tab
            key={SUBSCRIPTION_BILLING_PERIOD_TYPE.MONTHLY}
            title={t("subscription:page.payMonthly")}
            className="bg-transparent py-2 mob:py-5 mob:pb-6"
          />
        </Tabs>

        <Spacer className="!mt-10 mob:!mt-14" />
        <Row gap={2} className="flex-wrap justify-center items-end">
          {selectedPurchaseOptions?.map((planData, idx) => {
            if (
              !(isSubscribeUsingCryptoPage && effectiveFrequency === SUBSCRIPTION_BILLING_PERIOD_TYPE.MONTHLY && planData.role === USER_ROLES.MINIMAL)
            ) {
              const prevTier = selectedPurchaseOptions?.[idx - 1];
              const { tier } = getPurchaseData(prevTier);

              return <SubscriptionCard key={idx} planData={planData} prevTier={tier} purchaseOptions={purchaseOptions} />;
            }
          })}
        </Row>

        <Spacer className="!mt-[28px] mob:!mt-[56px]" />

        {!!purchaseOptions?.payment_methods?.length && (
          <>
            <PaymentMethodsDisplay paymentMethods={purchaseOptions.payment_methods} />
            <Spacer className="!mt-[28px] mob:!mt-[56px]" />
          </>
        )}

        <Spacer y={8} />

        <SubscriptionFAQs />

        <PlanComparisonTable tabledata={selectedPurchaseOptions} frequency={effectiveFrequency} />
      </Container>
    </>
  );
};

export default Subscribe;
